import React, { useState, useEffect, useRef } from "react";
import { Booking, User, TrekDate } from "../entities/all";
import { scrollToError } from "../components/navigation/ScrollToError";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CalendarIcon, Users, Phone, Mail, Shield, ShieldCheck, ShieldClose, Ban, Clock, Check, Trash2, LogIn } from "lucide-react";
import { format, parseISO, isAfter, startOfDay, differenceInCalendarDays } from "date-fns";
import AddDateForm from "../admin/AddDateForm";
import ManageDates from "../admin/ManageDatesForm";
import { useLanguage } from "../layout";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function MyBookingsPage() {
  const { language, isRTL } = useLanguage();
  const { login } = useAuth();
  const [myBookings, setMyBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddDate, setShowAddDate] = useState(false);
  const [trekDates, setTrekDates] = useState([]);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [selectedBookingToCancel, setSelectedBookingToCancel] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [isApproving, setIsApproving] = useState(null); // Store booking ID being approved
  const cancellationTextareaRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    setIsAuthError(false);
    try {
      // Load current user
      const user = await User.me();
      setCurrentUser(user);
      
      try {
        // Load user's own bookings and trek dates concurrently
        const [userBookings, allTrekDates] = await Promise.all([
          Booking.filter({ sort: '-trek_date', user_id: user.id }),
          TrekDate.list('-start_date')
        ]);
        
        setMyBookings(userBookings);
        setTrekDates(allTrekDates);
        
        // If user is admin, load all bookings
        if (user.role === 'admin') {
          try {
            const allBookingsData = await Booking.filter({ 
              sort: '-trek_date',
              admin: true // Use admin=true parameter for admin requests
            });
            console.log('Admin bookings loaded:', allBookingsData);
            setAllBookings(allBookingsData);
          } catch (error) {
            console.error('Error loading all bookings:', error);
            setError('Failed to load all bookings. Please try again.');
            scrollToError();
            setAllBookings([]);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load bookings. Please try again.');
        scrollToError();
        setMyBookings([]);
        setTrekDates([]);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      if (error.name === 'AuthError') {
        setIsAuthError(true);
        setError(language === 'he' ? 'פג תוקף החיבור שלך. אנא התחבר מחדש.' : 'Your session has expired. Please log in again.');
        scrollToError();
      } else {
        setError('Failed to load user data. Please try again.');
        scrollToError();
      }
    }
    setLoading(false);
  };

  const handleApproveBooking = async (bookingToApprove) => {
    setIsApproving(bookingToApprove.id);
    try {
      await Booking.update(bookingToApprove.id, { status: 'confirmed' });
      await loadData(); // Reload all data after update
    } catch (error) {
      console.error("Failed to approve booking:", error);
      setError('Failed to approve booking. Please try again.');
      scrollToError();
    } finally {
      setIsApproving(null);
    }
  };

  const handleCancelBooking = async (bookingToCancel, cancellationReason = '') => {
    setIsCancelling(true);
    try {
      // Update booking status to 'cancelled' with optional reason
      // Backend automatically handles restoring spots to trek date
      const updateData = { 
        status: 'cancelled',
        ...(cancellationReason && { cancellation_reason: cancellationReason })
      };
      await Booking.update(bookingToCancel.id, updateData);

      // Refresh data to get updated booking and trek date info
      await loadData();
      
      // Close dialog and reset state after successful cancellation
      setSelectedBookingToCancel(null);
      setCancellationReason('');
      // Clear textarea ref
      if (cancellationTextareaRef.current) {
        cancellationTextareaRef.current.value = '';
      }
    } catch (error) {
      console.error("Failed to cancel booking:", error);
      setError('Failed to cancel booking. Please try again.');
      scrollToError();
    } finally {
      setIsCancelling(false);
    }
  };

  const handleDeleteDate = async (dateId) => {
    try {
      await TrekDate.delete(dateId);
      await loadData();
    } catch (error) {
      console.error("Failed to delete date:", error);
      setError('Failed to delete date. Please try again.');
      scrollToError();
    }
  };

  const content = {
    he: {
      title: "ניהול הזמנות ותאריכים",
      myBookings: "ההזמנות שלי",
      allBookings: "כל ההזמנות",
      manageDates: "ניהול תאריכים",
      noBookings: "אין הזמנות עדיין",
      noBookingsDesc: "כשתבצע הזמנות, הן יופיעו כאן",
      noBookingsAdmin: "אין הזמנות במערכת עדיין",
      noBookingsAdminDesc: "כשלקוחות יבצעו הזמנות, הן יופיעו כאן",
      bookingDetails: "פרטי הזמנה",
      // ...existing code...
      status: {
        pending: "ממתין לאישור",
        confirmed: "מאושר",
        cancelled: "מבוטל"
      },
      contact: "פרטי קשר",
      emergency: "איש קשר לחירום",
      totalBookings: "סה״כ הזמנות",
      totalRevenue: "סה״כ הכנסות",
      totalParticipants: "סה״כ משתתפים",
      adminPanel: "פאנל ניהול",
      addNewDate: "הוסף תאריך חדש",
      hideAddDate: "סגור טופס",
      approveBooking: "אשר הזמנה",
      approvingBooking: "מאשר...",
      cancelBooking: "בטל הזמנה",
      cancelConfirmTitle: "האם לבטל את ההזמנה?",
      cancelConfirmDesc: "פעולה זו תבטל את הזמנתך באופן סופי. המקומות שהזמנת ישוחררו. האם להמשיך?",
      cancellationReason: "סיבת הביטול (אופציונלי)",
      cancellationReasonPlaceholder: "הסבר את הסיבה לביטול ההזמנה...",
      confirm: "אשר",
      confirming: "מבטל...",
      back: "חזור",
    },
    en: {
      title: "Manage Bookings & Dates",
      myBookings: "My Bookings", 
      allBookings: "All Bookings",
      manageDates: "Manage Dates",
      noBookings: "No bookings yet",
      noBookingsDesc: "When you make bookings, they will appear here",
      noBookingsAdmin: "No bookings in the system yet",
      noBookingsAdminDesc: "When customers make bookings, they will appear here",
      bookingDetails: "Booking Details",
      // ...existing code...
      status: {
        pending: "Pending Confirmation",
        confirmed: "Confirmed", 
        cancelled: "Cancelled"
      },
      contact: "Contact Details",
      emergency: "Emergency Contact",
      totalBookings: "Total Bookings",
      totalRevenue: "Total Revenue",
      totalParticipants: "Total Participants",
      adminPanel: "Admin Panel",
      addNewDate: "Add New Date",
      hideAddDate: "Close Form",
      approveBooking: "Approve Booking",
      approvingBooking: "Approving...",
      cancelBooking: "Cancel Booking",
      cancelConfirmTitle: "Are you sure you want to cancel?",
      cancelConfirmDesc: "This will permanently cancel your booking and release your spots. This action cannot be undone.",
      cancellationReason: "Cancellation Reason (Optional)",
      cancellationReasonPlaceholder: "Please explain the reason for cancelling this booking...",
      confirm: "Confirm",
      confirming: "Cancelling...",
      back: "Back",
    }
  };

  const currentContent = content[language];

  const BookingCard = ({ booking, showUserInfo = false }) => {
    // Check if the booking is cancelled
    const isCancelled = booking.status === 'cancelled';

    // Calculate days until trek date
    const trekDate = parseISO(booking.trek_date);
    const today = startOfDay(new Date());
    const daysUntilTrek = differenceInCalendarDays(trekDate, today);

    // user can cancel only if at least 3 days before trek
    const canUserCancel =
      !showUserInfo && booking.status !== "cancelled" && daysUntilTrek >= 3;

    // admin can manage any future (not yet started) trek
    const canAdminManage =
      showUserInfo && booking.status !== "cancelled" && isAfter(trekDate, today);

    // Define status display properties
    const statusInfo = {
      pending: {
        label: currentContent.status.pending,
        icon: <Clock className="w-5 h-5 text-yellow-600" />,
        badgeClass: "text-yellow-800",
      },
      cancelled: {
        label: currentContent.status.cancelled,
        icon: <ShieldClose className="w-5 h-5 text-red-500" />,
        badgeClass: "text-red-800",
      },
      confirmed: {
        label: currentContent.status.confirmed,
        icon: <ShieldCheck className="w-5 h-5 text-green-600" />,
        badgeClass: "text-green-800",
      }
    };

    // Get the current status display from the map, default to pending if not found
    const currentStatus = statusInfo[booking.status] || statusInfo.pending;

    return (
    <Card className={`bg-white/60 shadow-lg hover:shadow-xl transition-shadow duration-300 ${
      isCancelled 
        ? 'bg-desert-50 text-gray-500 opacity-80 shadow-none backdrop-blur-sm' : ''}`}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className={`text-xl font-bold text-gray-900`}>
              {showUserInfo ? `${booking.first_name} ${booking.last_name}` : currentContent.bookingDetails}
            </CardTitle>
            <p className="text-gray-500 text-sm">
              {language === 'he' ? 'הוזמן ב' : 'Booked on'}: {format(new Date(booking.created_date), 'MMM d, yyyy')}
            </p>
          </div>
          <Badge className={`${currentStatus.badgeClass} flex items-center gap-2`}>
            {currentStatus.icon}
            {currentStatus.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <CalendarIcon className="w-5 h-5 text-desert-600" />
            <div>
              <p className="font-medium">{format(new Date(booking.trek_date), 'MMM d, yyyy')}</p>
              <p className="text-sm text-gray-500">
                {language === 'he' ? 'תאריך טיול' : 'Trek Date'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Users className="w-5 h-5 text-desert-600" />
            <div>
              <p className="font-medium">{booking.participants_count} {language === 'he' ? 'אנשים' : 'people'}</p>
              <p className="text-sm text-gray-500">
                ₪{booking.total_price?.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {showUserInfo && (
          <div className="pt-4 border-t border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-2">{currentContent.contact}</h4>
            <div className="grid md:grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{booking.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{booking.phone}</span>
              </div>
            </div>
            
            {booking.emergency_contact_name && (
              <div className="mt-3">
                <h5 className="font-medium text-gray-700 text-sm">{currentContent.emergency}:</h5>
                <p className="text-sm text-gray-600">
                  {booking.emergency_contact_name} - {booking.emergency_contact_phone}
                </p>
              </div>
            )}
          </div>
        )}

        {booking.special_requests && (
          <div className="pt-2 border-t border-gray-100">
            <h4 className="font-semibold text-gray-900 mb-1 text-sm">
              {language === 'he' ? 'בקשות מיוחדות' : 'Special Requests'}:
            </h4>
            <p className="text-sm text-gray-600">{booking.special_requests}</p>
          </div>
        )}
      </CardContent>
      {showUserInfo && canAdminManage && (
        <CardFooter className="grid grid-cols-2 gap-2">
          {booking.status === 'pending' && (
             <Button 
               variant="default" 
               size="sm" 
               onClick={() => handleApproveBooking(booking)} 
               className="bg-green-600 hover:bg-green-700 text-white min-w-[120px]"
               disabled={isApproving === booking.id}
             >
               {isApproving === booking.id ? (
                 <div className="flex items-center gap-2">
                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                   {currentContent.approvingBooking}
                 </div>
               ) : (
                 <div className="flex items-center gap-2">
                   <Check className="w-4 h-4" />
                   {currentContent.approveBooking}
                 </div>
               )}
              </Button>
          )}
          <Button 
            variant="destructive" 
            size="sm" 
            className={booking.status === 'pending' ? '' : 'col-span-2'}
            onClick={() => setSelectedBookingToCancel(booking)}
          >
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              {currentContent.cancelBooking}
            </div>
          </Button>
        </CardFooter>
      )}
      {!showUserInfo && canUserCancel && (
        <CardFooter>
          <Button 
            variant="destructive" 
            size="sm" 
            className="w-full"
            onClick={() => setSelectedBookingToCancel(booking)}
          >
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4" />
              {currentContent.cancelBooking}
            </div>
          </Button>
        </CardFooter>
      )}
    </Card>
  )};

  // Cancellation Dialog Component - outside of the mapping loop
  const CancellationDialog = () => (
    <AlertDialog 
      open={!!selectedBookingToCancel} 
      onOpenChange={(open) => {
        // Only allow closing if not currently cancelling
        if (!open && !isCancelling) {
          setSelectedBookingToCancel(null);
          setCancellationReason('');
          // Clear textarea when dialog closes
          if (cancellationTextareaRef.current) {
            cancellationTextareaRef.current.value = '';
          }
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{currentContent.cancelConfirmTitle}</AlertDialogTitle>
          <AlertDialogDescription>{currentContent.cancelConfirmDesc}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="my-4">
          <Label htmlFor="cancellationReasonDialog">{currentContent.cancellationReason}</Label>
          <Textarea
            ref={cancellationTextareaRef}
            id="cancellationReasonDialog"
            dir={isRTL ? "rtl" : "ltr"}
            defaultValue={cancellationReason}
            placeholder={currentContent.cancellationReasonPlaceholder}
            className={`mt-2 ${isRTL ? "text-right" : "text-left"}`}
            rows={3}
            autoFocus
            disabled={isCancelling}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel 
            onClick={() => {
              if (!isCancelling) {
                setSelectedBookingToCancel(null);
                setCancellationReason('');
              }
            }}
            disabled={isCancelling}
          >
            {currentContent.back}
          </AlertDialogCancel>
          <Button
            onClick={() => {
              if (selectedBookingToCancel && !isCancelling) {
                const reason = cancellationTextareaRef.current?.value?.trim() || '';
                handleCancelBooking(selectedBookingToCancel, reason);
              }
            }}
            disabled={isCancelling}
            className="min-w-[100px] bg-red-600 hover:bg-red-700 text-white"
          >
            {isCancelling ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {currentContent.confirming}
              </div>
            ) : (
              currentContent.confirm
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  if (loading) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-desert-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{language === 'he' ? 'טוען...' : 'Loading...'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center error-message">
            <div className="text-red-600 mb-4">⚠️</div>
            <p className="text-gray-900 font-semibold mb-4">{error}</p>
            {isAuthError ? (
              <Button 
                onClick={login}
                className="bg-desert-600 hover:bg-desert-700 inline-flex items-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                {language === 'he' ? 'התחבר מחדש' : 'Log In Again'}
              </Button>
            ) : (
              <Button 
                onClick={loadData} 
                className="mt-4 bg-desert-600 hover:bg-desert-700"
              >
                {language === 'he' ? 'נסה שוב' : 'Try Again'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="min-h-screen py-12 bg-gradient-to-b from-desert-50 to-orange-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-desert-800 mb-6">
            {currentContent.title}
          </h1>
          {isAdmin && (
            <div className="flex items-center justify-center gap-2 text-desert-700 mb-4">
              <Shield className="w-5 h-5" />
              <span className="font-medium">{currentContent.adminPanel}</span>
            </div>
          )}
        </div>

        <Tabs defaultValue="my-bookings" className="space-y-8">
          <TabsList className={`grid w-full max-w-md mx-auto ${isAdmin ? 'grid-cols-3' : 'grid-cols-1'}`}>
            <TabsTrigger value="my-bookings">{currentContent.myBookings}</TabsTrigger>
            {isAdmin && (
              <>
                <TabsTrigger value="all-bookings">{currentContent.allBookings}</TabsTrigger>
                <TabsTrigger value="manage-dates">{currentContent.manageDates}</TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="my-bookings" className="space-y-6">
            {myBookings.length > 0 ? (
              <div className="grid lg:grid-cols-2 gap-6">
                {myBookings.map((booking) => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CalendarIcon className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {currentContent.noBookings}
                </h3>
                <p className="text-gray-600">
                  {currentContent.noBookingsDesc}
                </p>
              </div>
            )}
          </TabsContent>

          {isAdmin && (
            <TabsContent value="all-bookings" className="space-y-6">
              {/* Admin Statistics */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <Card className="border-none bg-white/60 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-desert-600 mb-2">
                      {allBookings.filter(b => b.status !== 'cancelled').length}
                    </div>
                    <p className="text-gray-600">{currentContent.totalBookings}</p>
                  </CardContent>
                </Card>
                <Card className="border-none bg-white/60 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      ₪{allBookings.filter(b => b.status === 'confirmed').reduce((sum, booking) => sum + (booking.total_price || 0), 0).toLocaleString()}
                    </div>
                    <p className="text-gray-600">{currentContent.totalRevenue}</p>
                  </CardContent>
                </Card>
                <Card className="border-none bg-white/60 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {allBookings.filter(b => b.status === 'confirmed').reduce((sum, booking) => sum + (booking.participants_count || 0), 0)}
                    </div>
                    <p className="text-gray-600">
                      {currentContent.totalParticipants}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {allBookings.length > 0 ? (
                <div className="grid lg:grid-cols-2 gap-6">
                  {allBookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} showUserInfo={true} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {currentContent.noBookingsAdmin}
                  </h3>
                  <p className="text-gray-600">
                    {currentContent.noBookingsAdminDesc}
                  </p>
                </div>
              )}
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="manage-dates" className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  {currentContent.manageDates}
                </h2>
                <Button
                  onClick={() => setShowAddDate(!showAddDate)}
                  className="text-white hover:opacity-90"
                >
                  {showAddDate ? currentContent.hideAddDate : currentContent.addNewDate}
                </Button>
              </div>

              {showAddDate && (
                <AddDateForm 
                  onDateAdded={() => {
                    setShowAddDate(false);
                    loadData(); // Refresh data after adding a date
                  }}
                  language={language}
                />
              )}
              <ManageDates trekDates={trekDates} onDeleteDate={handleDeleteDate} language={language} />
            </TabsContent>
          )}
        </Tabs>
      </div>
      <CancellationDialog />
    </div>
  );
}
